import { computed, inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { parseApiDate } from '@webapp/core';
import { StoreService } from '@webapp/core/services/store.service';
import { firstValueFrom } from 'rxjs';
import { ApiKey, ApiKeyRow } from '../interfaces/api-keys.interface';
import { ApiKeysService } from '../services/api-keys.service';
import { isApiKeyExpired, MAX_ACTIVE_API_KEYS } from '../utils/api-keys.utils';

export interface ApiKeysState {
	keys: ApiKey[];
	loading: boolean;
	loadError: boolean;
	/** Reference time for the "expired" computation, refreshed on every load. */
	now: Date;
}

export type DeleteApiKeyResult = 'deleted' | 'not-found';

/**
 * List state only. The plaintext key returned by POST is owned by the create
 * modal and must never reach this store, so there is intentionally no create
 * method here. After a create, callers refresh with `load()` (GET).
 * Errors are caught WITHOUT logging (see design: leak analysis).
 */
@Injectable()
export class ApiKeysStore extends StoreService<ApiKeysState> {
	private apiKeysService = inject(ApiKeysService);

	constructor() {
		super();
		this.setState({ keys: [], loading: false, loadError: false, now: new Date() });
	}

	rows = computed<ApiKeyRow[]>(() => {
		const now = this.select('now')();
		return this.select('keys')().map(key => ({
			...key,
			createdAtDate: parseApiDate(key.createdAt),
			lastUsedAtDate: key.lastUsedAt ? parseApiDate(key.lastUsedAt) : null,
			expiresAtDate: key.expiresAt ? parseApiDate(key.expiresAt) : null,
			expired: isApiKeyExpired(key, now),
		}));
	});
	activeCount = computed(() => this.rows().filter(row => !row.expired).length);
	canCreate = computed(() => this.activeCount() < MAX_ACTIVE_API_KEYS);

	async load(): Promise<void> {
		this.set('loading', true);

		try {
			const keys = await firstValueFrom(this.apiKeysService.list());
			this.setState({ keys, now: new Date(), loadError: false });
		} catch {
			this.set('loadError', true);
		} finally {
			this.set('loading', false);
		}
	}

	/**
	 * Deletes and refreshes via GET. A 404 (already gone) also refreshes and is
	 * reported as 'not-found'; any other error is rethrown for the caller.
	 */
	async deleteKey(id: string): Promise<DeleteApiKeyResult> {
		try {
			await firstValueFrom(this.apiKeysService.delete(id));
		} catch (error) {
			if (error instanceof HttpErrorResponse && error.status === 404) {
				await this.load();
				return 'not-found';
			}
			throw error;
		}
		await this.load();
		return 'deleted';
	}
}
